import { createFileRoute, Link } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Check } from "lucide-react";

import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { MobileStickyCta } from "@/components/marketing/MobileStickyCta";
import { Section } from "@/components/marketing/layout/Section";
import { Features } from "@/components/marketing/home/Features";
import { DataSourcesShowcase } from "@/components/marketing/home/DataSourcesShowcase";
import { CaseStudyStrip } from "@/components/marketing/home/CaseStudyStrip";
import { SolutionCta } from "@/components/marketing/solution/SolutionCta";
import {
  PROBLEMS,
  SOLUTION_DETAIL,
} from "@/components/marketing/solution/problems";
import {
  CONTACT_SOURCE_TAGS,
  PRIMARY_CTA_LABEL,
  QUALIFIER_PATH,
} from "@/components/marketing/solution/constants";

export const Route = createFileRoute("/solutions")({
  component: SolutionsPage,
});

/**
 * The solutions landing page.
 *
 * ORDER IS THE POINT. It opens on the business problem and stays there for the
 * whole first half - six sections about what has actually gone wrong in an
 * operation, mined from the archived home page and the message that replaced it.
 * Only below those does it turn to products and features, reusing the same
 * `Features` and `DataSourcesShowcase` blocks the archived home page used. A
 * reader who arrives from a search about their problem should recognise
 * themselves before they are told what we sell.
 *
 * It is a landing page, not a destination: every section hands off to the page
 * that goes deeper, and those targets are declared alongside the copy in
 * `problems.ts`.
 *
 * This replaced a `ComingSoon` stub, so the `noindex` that stub carried is gone
 * and the route now appears in the sitemap and in PRERENDER_ROUTES.
 */

const SERVICE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Larasoft Solutions",
  description:
    "Larasoft connects the systems a business already runs into one database it owns, then answers questions of that data and automates the work in between.",
  url: "https://larasoft.global/solutions",
  provider: {
    "@type": "Organization",
    name: "Larasoft",
    url: "https://larasoft.global",
  },
};

function SolutionsPage() {
  return (
    <MarketingLayout>
      <Helmet>
        <title>Solutions - fix what your systems cannot see | Larasoft</title>
        <link rel="canonical" href="https://larasoft.global/solutions" />
        <meta
          name="description"
          content="Six problems we solve: systems that disagree, work that depends on someone remembering, answers that need a specialist, debtors, reconciliation and multi-entity month-end."
        />
        <script type="application/ld+json">
          {JSON.stringify(SERVICE_SCHEMA)}
        </script>
      </Helmet>

      {/* Hero - business framing only. No product names above this line. */}
      <Section className="py-14 sm:py-20">
        <div className="max-w-3xl space-y-6">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-blue-medium">
            Solutions
          </p>
          <h1 className="text-4xl font-extrabold leading-[1.1] text-brand-blue-dark sm:text-5xl">
            Start with what is{" "}
            <span className="text-brand-blue-medium">going wrong.</span>
          </h1>
          <p className="text-lg text-slate-600">
            Most organisations do not need another system. They need the ones
            they already run to agree with each other, and the work in between to
            stop depending on somebody remembering it.
          </p>
          <p className="text-slate-600">
            These are the six problems we are asked to solve most often. Find the
            one that sounds like your week, and see what changes.
          </p>
          <div className="flex flex-wrap gap-3 pt-2">
            <SolutionCta
              event="hero_primary_cta"
              eventDetail="solutions"
              search={{ source: CONTACT_SOURCE_TAGS.home }}
            >
              {PRIMARY_CTA_LABEL}
            </SolutionCta>
            <SolutionCta
              to={QUALIFIER_PATH}
              event="hero_secondary_cta"
              eventDetail="solutions"
              variant="outline"
            >
              Do you qualify for a free proof of concept?
            </SolutionCta>
          </div>
        </div>
      </Section>

      <MobileStickyCta />

      {/* Contents. Six anchors is enough to be worth skipping between, and it
          gives the home page's cards a visible destination on arrival. */}
      <Section className="pb-4 pt-0">
        <nav aria-label="Problems on this page">
          <ul className="flex flex-wrap gap-2">
            {PROBLEMS.map((problem) => (
              <li key={problem.id}>
                <a
                  href={`#${problem.id}`}
                  className="inline-flex rounded-full border border-brand-blue-pale/60 bg-white/60 px-4 py-2 text-sm text-slate-600 hover:border-brand-blue-medium hover:text-brand-blue-dark"
                >
                  {problem.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </Section>

      {/* The six, long form. Alternating background so consecutive sections
          stay distinguishable while scrolling. */}
      {PROBLEMS.map((problem, index) => {
        const detail = SOLUTION_DETAIL[problem.id];
        return (
          <Section
            key={problem.id}
            id={problem.id}
            containerClassName="space-y-6"
            className={
              index % 2 === 0 ? "py-14 sm:py-16" : "bg-slate-50 py-14 sm:py-16"
            }
          >
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[3fr_2fr]">
              <div className="space-y-4">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-blue-medium">
                  Problem {index + 1} of {PROBLEMS.length}
                </p>
                <h2 className="text-3xl font-extrabold text-brand-blue-dark sm:text-4xl">
                  {problem.title}
                </h2>
                <p className="text-lg italic text-slate-500">
                  “{problem.quote}”
                </p>
                <p className="text-lg text-slate-600">{detail.lead}</p>
              </div>

              <div className="space-y-5">
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-brand-blue-dark">
                    What we do
                  </p>
                  <ul className="space-y-3">
                    {detail.how.map((step) => (
                      <li key={step} className="flex gap-3 text-slate-600">
                        <Check
                          className="mt-1 h-4 w-4 shrink-0 text-brand-blue-medium"
                          aria-hidden="true"
                        />
                        <span>{step}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex flex-col gap-2">
                  {detail.links.map((link) => (
                    <Link
                      key={`${link.to}${link.hash ?? ""}`}
                      to={link.to}
                      hash={link.hash}
                      className="inline-flex items-center gap-1 font-semibold text-brand-blue-dark hover:underline"
                    >
                      {link.label}
                      <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </Section>
        );
      })}

      {/* THE TURN. Everything above is the business problem; everything below is
          what we build with. */}
      <Section
        containerClassName="max-w-3xl space-y-3"
        className="pt-16 sm:pt-20"
      >
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-blue-medium">
          What we build with
        </p>
        <h2 className="text-3xl font-extrabold text-brand-blue-dark sm:text-4xl">
          The same three products solve all six
        </h2>
        <p className="text-lg text-slate-600">
          Nothing above needs a bespoke platform. Every one of those problems is
          some combination of connecting systems, capturing what no system owns,
          and being able to ask the result a question.
        </p>
      </Section>

      <Features />

      <DataSourcesShowcase showCta />

      <CaseStudyStrip />

      {/* Close */}
      <Section
        containerClassName="max-w-3xl space-y-5 text-center"
        className="py-16 sm:py-20"
      >
        <h2 className="text-3xl font-extrabold text-brand-blue-dark sm:text-4xl">
          Which one is costing you the most?
        </h2>
        <p className="text-lg text-slate-600">
          Start with a short call about that one. If we can see the win, you
          qualify for a free proof of concept on your own data.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <SolutionCta
            event="final_cta"
            eventDetail="solutions"
            search={{ source: CONTACT_SOURCE_TAGS.home }}
          >
            {PRIMARY_CTA_LABEL}
          </SolutionCta>
          <SolutionCta
            to={QUALIFIER_PATH}
            event="final_cta"
            eventDetail="solutions-qualifier"
            variant="outline"
          >
            Check if you qualify
          </SolutionCta>
        </div>
      </Section>
    </MarketingLayout>
  );
}
